import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { lazy, Suspense } from "react";
import { BrowserRouter, Route, Routes } from "react-router";
import { PageViewTracker } from "@viajero/shared-ui/analytics";
import { AppLayout } from "@/components/layout/AppLayout";
import { NPSTokenLayout } from "@/components/layout/NPSTokenLayout";
import { ReservationTokenLayout } from "@/components/layout/ReservationTokenLayout";
import { ScrollToTop } from "@/components/layout/ScrollToTop";
import { CourseDetailLayout } from "@/features/courses/components/CourseDetailLayout";
import { isShopDetailPath, redactPath } from "@/lib/analytics/redactPath";

// ルートごとに dynamic import で分割し、初期ロードを軽くする (ADR-009)。
const MainSiteRedirect = lazy(() => import("@/app/routes/MainSiteRedirect"));
const ShopDetailPage = lazy(() => import("@/app/routes/ShopDetailPage"));
const CourseDetailPage = lazy(() => import("@/app/routes/CourseDetailPage"));
const BuyerInfoPage = lazy(() => import("@/app/routes/BuyerInfoPage"));
const ReservationCompletePage = lazy(() => import("@/app/routes/ReservationCompletePage"));
const CancelConfirmPage = lazy(() => import("@/app/routes/CancelConfirmPage"));
const CancelCompletePage = lazy(() => import("@/app/routes/CancelCompletePage"));
const NPSPage = lazy(() => import("@/app/routes/NPSPage"));

// 遅延ルート (React.lazy) の読み込み中フォールバック。画面中央に CircularProgress を表示する。
function RouteFallback() {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
      }}
    >
      <CircularProgress />
    </Box>
  );
}

export function AppRouter() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      {/* ルート遷移ごとの page_view (JPTP_CANSEI-1400)。店舗詳細だけは page_view に店舗名を
          載せるため ShopDetailView 側で送る (isShopDetailPath で除外して二重計上を防ぐ)。 */}
      <PageViewTracker redactPath={redactPath} shouldSkip={isShopDetailPath} />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* AppLayout を親ルートとし、すべてのページを Header + Footer で囲む。
              URL 設計は /{caseToken}/... (docs/design/ゲストアプリの設計.md §4.2)。 */}
          <Route element={<AppLayout />}>
            <Route path="/:caseToken/:shopId" element={<ShopDetailPage />} />
            {/* コース詳細配下は CourseDetailLayout でネストする */}
            <Route path="/:caseToken/:shopId/:productId" element={<CourseDetailLayout />}>
              <Route index element={<CourseDetailPage />} />
              <Route path="checkout" element={<BuyerInfoPage />} />
            </Route>
            {/* 予約完了・キャンセル画面は決済成功後の success_url / キャンセルメールのリンク
                (/reservations/{予約トークン}/...。§7.1 / §6.5)。案件アクセストークンを持たず、
                URL の予約アクセストークンで GetReservation / CancelReservation を認可する。 */}
            <Route path="/reservations/:reservationToken" element={<ReservationTokenLayout />}>
              <Route path="complete" element={<ReservationCompletePage />} />
              <Route path="cancel" element={<CancelConfirmPage />} />
              <Route path="cancel/complete" element={<CancelCompletePage />} />
            </Route>
            {/* NPS アンケート画面はアンケート依頼メールのリンク (/nps/{npsToken}。§6.7)。
                案件アクセストークンを持たず、URL の npsToken で GetNPSContext /
                SubmitNPSAnswer を認可する。静的セグメント `nps` を持つため
                /:caseToken/:shopId より優先して一致する。 */}
            <Route path="/nps/:npsToken" element={<NPSTokenLayout />}>
              <Route index element={<NPSPage />} />
            </Route>
            <Route path="*" element={<MainSiteRedirect />} />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
